import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", update);
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="w-full md:w-1/2 p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-[var(--color-secondary-muted)] rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-base text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const GovernanceAndRoleManagement = () => (
  <div className="relative overflow-hidden space-y-8 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Introduction */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-6xl mx-auto space-y-4 text-center"
    >
      <h2 className="text-3xl font-bold text-primary-dark text-center">
        Governance and Role Management in UcWaves
      </h2>
      <Paragraph className="text-gray-700 max-w-6xl mx-auto">
        Trust and decentralization are foundational to Uncensored Waves
        (UcWaves). To uphold these principles, UcWaves employs a carefully
        orchestrated governance and role-management framework. This includes
        timed renunciation of permissions, segregation of duties, and a clear
        path toward full DAO-based governance.
      </Paragraph>
    </motion.div>

    {/* Two-column layout */}
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
      {/* Left block: Sections 1 + 2 */}
      <SectionCard title="1. Timed Ownership Renunciation">
        <Paragraph className="font-semibold">
          1.1 Deployer Admin Wallet
        </Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            <strong>Purpose:</strong> During initial deployment, a Deployer
            Admin Wallet holds highest-level privileges for configuration, role
            assignments, and funding.
          </li>
          <br />
          <li>
            <strong>Renunciation:</strong> After verification and parameter
            setup, UcWaves will renounce ownership of core contracts, giving up on
            admin privileges to prevent further changes.
          </li>
        </ul>

        <Paragraph className="font-semibold mt-4">
          1.2 Security Implications
        </Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            <strong>Immutable Core Logic:</strong> With ownership renounced,
            core contract logic and parameters cannot be altered.
          </li>
          <br />
          <li>
            <strong>Community Trust:</strong> Public renunciation demonstrates
            commitment to decentralization.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          2. Segregated Staking Administration
        </h3>
        <Paragraph className="font-semibold">2.1 StakingOP Wallet</Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            <strong>Role:</strong> Retains only the admin role for staking
            contracts; no minting or burning control.
          </li>
          <br />
          <li>
            <strong>Initial Funding:</strong> Receives 350 million NAU, 5
            million NAUY, and 5 million NAUN to seed rewards.
          </li>
        </ul>

        <Paragraph className="font-semibold mt-4">
          2.2 Purpose of Separation
        </Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            <strong>Least Privilege:</strong> Limits risk by restricting wallet
            to reward-rate adjustments only.
          </li>
          <br />
          <li>
            <strong>Flexibility:</strong> Allows dynamic tuning of reward rates
            until full on-chain governance.
          </li>
        </ul>
      </SectionCard>

      {/* Right block: Sections 3 + 4 */}
      <SectionCard title="3. Independent Staking Contracts">
        <Paragraph>
          UcWaves launches three distinct staking contracts, each with its own
          token and reward stream:
        </Paragraph>
        <ul className="list-disc list-outsideside text-base text-gray-800">
          <li>NAU Staking (stake NAU → earn NAU)</li>
          <br />
          <li>NAUY Staking (stake NAUY → earn NAUY)</li>
          <br />
          <li>NAUN Staking (stake NAUN → earn NAUN)</li>
          <br />
        </ul>

        <Paragraph className="font-semibold mt-4">
          3.1 Funding Allocation
        </Paragraph>
        <ul className="list-disc list-side text-base text-gray-800">
          <li>
            Proportional Distribution: Initial tokens in StakingOP wallet are
            allocated to contracts reflecting anticipated demand and community
            sentiment.
          </li>
        </ul>

        <Paragraph className="font-semibold mt-4">
          3.2 Administrative Controls
        </Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            <strong>Reward Rate Adjustment:</strong> StakingOP can call{" "}
            <code>setRewardRate()</code> to tune emissions.
          </li>
          <br />
          <li>
            <strong>Role Revocation:</strong> Once stable, staking-admin role
            transfers to DAO.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          4. Roadmap to DAO Governance
        </h3>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            <strong>Phase 1:</strong> Deployment, initialization, and staking
            seeding.
          </li>
          <br />
          <li>
            <strong>Phase 2:</strong> Ownership renunciation and staking-admin
            segregation.
          </li>
          <li>
            <strong>Phase 3:</strong> Community onboarding and governance
            discussions.
          </li>
          <br />
          <li>
            <strong>Phase 4:</strong> Transfer of admin roles and parameters to
            DAO.
          </li>
          <li>
            <strong>Phase 5:</strong> Full decentralization via on-chain
            governance.
          </li>
        </ul>
      </SectionCard>
    </div>

    {/* Conclusion */}
    <div className="max-w-6xl mx-auto">
      <Paragraph className="text-gray-700 max-w-6xl mx-auto text-justify">
        UcWaves’ governance design uses timed renunciation, role segregation,
        and a clear DAO migration plan to achieve secure, transparent
        decentralization and operational flexibility.
      </Paragraph>
    </div>
  </div>
);

export default GovernanceAndRoleManagement;
